import React, { useCallback, useEffect, useState } from 'react'
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileCheck2,
  Network,
  Plus,
  RefreshCw,
  Save,
  Trash2,
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { useToast } from '@/contexts/ToastContext'
import {
  projectsApi,
  type TestProcedureIntegrationBoundary,
  type TestProcedureIntegrationConfig,
  type TestProcedureSourceReview,
  type TestProcedureSourceSystem,
  type TestProcedureEvidence,
  type TestProcedureResearchReview,
  type TestProcedureResearchRun,
  type TestProcedureOutput,
  type TestProcedureState,
  type TestProcedureSystemApplicabilityReview,
  type TestProcedureTarget,
} from '@/lib/projectsApi'

interface Props {
  projectId: string
  projectHull: string
  initialProfileCode: string | null
  canEdit: boolean
  canGenerate: boolean
}

const fieldClass = 'w-full rounded-md border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-800 outline-none focus:border-blue-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

export const TestProcedureWorkspace: React.FC<Props> = ({
  projectId,
  initialProfileCode,
  canEdit,
  canGenerate,
}) => {
  const { success, error: showError } = useToast()
  const [state, setState] = useState<TestProcedureState | null>(null)
  const [loading, setLoading] = useState(true)
  const [working, setWorking] = useState(false)
  const [selectedSystemIds, setSelectedSystemIds] = useState<Set<string>>(new Set())
  const [selectionDirty, setSelectionDirty] = useState(false)
  const [, setResearchRuns] = useState<TestProcedureResearchRun[]>([])
  const [activeRun, setActiveRun] = useState<TestProcedureResearchRun | null>(null)
  const [researchReview, setResearchReview] = useState<TestProcedureResearchReview | null>(null)
  const [researchEvidence, setResearchEvidence] = useState<TestProcedureEvidence[]>([])
  const [outputs, setOutputs] = useState<TestProcedureOutput[]>([])

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [nextState, runs, storedOutputs] = await Promise.all([
        projectsApi.getTestProcedureState(projectId, initialProfileCode),
        projectsApi.listTestProcedureResearchRuns(projectId),
        projectsApi.listTestProcedureOutputs(projectId),
      ])
      setState(nextState)
      setResearchRuns(runs)
      setActiveRun(runs[0] ?? null)
      setOutputs(storedOutputs)
      setSelectedSystemIds(new Set(
        nextState.selection_targets
          .filter(target => target.included)
          .map(target => target.id),
      ))
      setSelectionDirty(false)
    } catch (error: any) {
      showError(error?.message ?? 'Failed to load the Test Procedure workspace')
    } finally {
      setLoading(false)
    }
  }, [initialProfileCode, projectId, showError])

  useEffect(() => { void load() }, [load])

  const activeRunId = activeRun?.id ?? null
  const activeRunStatus = activeRun?.status ?? null
  useEffect(() => {
    if (!activeRunId) return
    let stopped = false
    const refresh = async () => {
      try {
        const next = await projectsApi.getTestProcedureResearchRun(projectId, activeRunId)
        if (stopped) return
        setActiveRun(next)
        setResearchRuns(current => current.map(item => item.id === next.id ? { ...item, ...next } : item))
        if (next.status === 'completed') {
          const [review, evidence] = await Promise.all([
            projectsApi.listTestProcedureClaims(projectId, next.id),
            projectsApi.listTestProcedureEvidence(projectId, next.id),
          ])
          if (!stopped) {
            setResearchReview(review)
            setResearchEvidence(evidence)
          }
        }
      } catch {
        // The primary load/error path remains visible; polling retries.
      }
    }
    void refresh()
    if (!activeRunStatus || !['queued', 'running'].includes(activeRunStatus)) return () => { stopped = true }
    const timer = window.setInterval(() => { void refresh() }, 2000)
    return () => {
      stopped = true
      window.clearInterval(timer)
    }
  }, [activeRunId, activeRunStatus, projectId])

  const refreshResearchReview = useCallback(async (runId: string) => {
    const [nextRun, review, evidence] = await Promise.all([
      projectsApi.getTestProcedureResearchRun(projectId, runId),
      projectsApi.listTestProcedureClaims(projectId, runId),
      projectsApi.listTestProcedureEvidence(projectId, runId),
    ])
    setActiveRun(nextRun)
    setResearchReview(review)
    setResearchEvidence(evidence)
    return { nextRun, review, evidence }
  }, [projectId])

  const research = async () => {
    if (!state) return
    setWorking(true)
    try {
      let prepared = state
      if (!selectedSystemIds.size) {
        throw new Error('Select at least one target system for the Test Procedure.')
      }
      if (!prepared.draft || selectionDirty) {
        prepared = await projectsApi.autoPrepareTestProcedure(
          projectId,
          state.profile.profile_code,
          [...selectedSystemIds],
        )
        setState(prepared)
        setSelectedSystemIds(new Set(
          prepared.selection_targets
            .filter(target => target.included)
            .map(target => target.id),
        ))
        setSelectionDirty(false)
      }
      const nextRun = await projectsApi.startTestProcedureResearch(
        projectId,
        prepared.profile.profile_code,
        [...selectedSystemIds],
      )
      setActiveRun(nextRun)
      setResearchRuns(current => [nextRun, ...current])
      setResearchReview(null)
      setResearchEvidence([])
      success('Internal evidence research started')
    } catch (error: any) {
      showError(error?.message ?? 'Failed to start Test Procedure research')
    } finally {
      setWorking(false)
    }
  }

  if (loading && !state) {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-800">
        <RefreshCw className="h-4 w-4 animate-spin" /> Loading Test Procedure workspace
      </div>
    )
  }

  if (!state) return null

  const navigationTargets = state.selection_targets.filter(target => target.group === 'navigation')
  const otherTargets = state.selection_targets.filter(target => target.group !== 'navigation')

  return (
    <div className="space-y-4">
      <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-white">
              <FileCheck2 className="h-5 w-5 text-blue-500" /> Cyber Security Test Procedure
            </h2>
            <p className="mt-1 max-w-3xl text-xs leading-relaxed text-slate-500 dark:text-slate-400">
              Researches current-project records and indexed internal documents. Unsupported facts remain TBU.
            </p>
          </div>
          <Button
            size="sm"
            disabled={!canGenerate || working || !selectedSystemIds.size || activeRun?.status === 'running' || activeRun?.status === 'queued'}
            onClick={() => void research()}
          >
            {working
              ? <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              : <FileCheck2 className="mr-1.5 h-3.5 w-3.5" />}
            {working ? 'Preparing…' : 'Research & Draft'}
          </Button>
        </div>

        <div className="mt-4 grid gap-2 text-xs sm:grid-cols-3">
          <StatusCard label="Class profile" value={`${state.profile.class_society} · ${state.profile.notation_qualifier || state.profile.notation_label}`} />
          <StatusCard
            label="Selected systems"
            value={`${selectedSystemIds.size} / ${state.selection_targets.length}`}
            ready={selectedSystemIds.size > 0}
          />
          <StatusCard
            label="Research"
            value={activeRun
              ? `${activeRun.status} · ${activeRun.completed_systems}/${activeRun.total_systems}`
              : 'Not started'}
            ready={activeRun?.status === 'completed'}
          />
        </div>
      </section>

      {activeRun && (
        <ResearchRunPanel
          run={activeRun}
          review={researchReview}
          evidence={researchEvidence}
          canEdit={canEdit}
          working={working}
          onCancel={async () => {
            setWorking(true)
            try {
              await projectsApi.cancelTestProcedureResearch(projectId, activeRun.id)
              setActiveRun(await projectsApi.getTestProcedureResearchRun(projectId, activeRun.id))
            } catch (error: any) {
              showError(error?.message ?? 'Failed to cancel research')
            } finally {
              setWorking(false)
            }
          }}
          onAcceptSupported={async () => {
            setWorking(true)
            let acceptanceCompleted = false
            try {
              const result = await projectsApi.acceptSupportedTestProcedureClaims(projectId, activeRun.id)
              acceptanceCompleted = true
              const { nextRun } = await refreshResearchReview(activeRun.id)
              const mode = nextRun.readiness?.ready ? 'class-ready' : 'draft'
              const output = await projectsApi.generateTestProcedureOutput(projectId, activeRun.id, mode)
              setOutputs(current => [output, ...current.filter(item => item.id !== output.id)])
              await projectsApi.downloadStoredTestProcedureOutput(projectId, output.id, output.output_filename)
              success(
                `Accepted ${result.accepted_claims} claims and ${result.accepted_decisions} decisions; `
                + `${mode === 'class-ready' ? 'class-ready Test Procedure' : 'cited Test Procedure draft'} downloaded`,
              )
            } catch (error: any) {
              if (acceptanceCompleted) {
                try {
                  const storedOutputs = await projectsApi.listTestProcedureOutputs(projectId)
                  setOutputs(storedOutputs)
                  const recovered = storedOutputs.find(output =>
                    output.research_run_id === activeRun.id && output.output_bytes != null)
                  if (recovered) {
                    await projectsApi.downloadStoredTestProcedureOutput(
                      projectId,
                      recovered.id,
                      recovered.output_filename,
                    )
                    success('Test Procedure generation completed on the server and the stored file was downloaded')
                    return
                  }
                } catch {
                  // Preserve the original generation error below.
                }
              }
              showError(error?.message ?? 'Failed to accept and generate the Test Procedure')
            } finally {
              setWorking(false)
            }
          }}
          onReviewClaim={async (claimId, status) => {
            await projectsApi.reviewTestProcedureClaim(projectId, claimId, status)
            await refreshResearchReview(activeRun.id)
          }}
          outputs={outputs}
          onGenerateOutput={async mode => {
            setWorking(true)
            try {
              const output = await projectsApi.generateTestProcedureOutput(projectId, activeRun.id, mode)
              setOutputs(current => [output, ...current])
              success(`${mode === 'draft' ? 'Research draft' : 'Class-ready output'} stored`)
            } catch (error: any) {
              showError(error?.message ?? 'Failed to generate stored output')
            } finally {
              setWorking(false)
            }
          }}
          onDownloadOutput={async output => {
            try {
              await projectsApi.downloadStoredTestProcedureOutput(projectId, output.id, output.output_filename)
            } catch (error: any) {
              showError(error?.message ?? 'Failed to download stored output')
            }
          }}
        />
      )}

      {state.selection_targets.length > 0 && (
        <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Target Systems</h3>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Select the systems to include. Eligible systems are selected by default.
              </p>
            </div>
            <label className="flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300">
              <input
                type="checkbox"
                checked={
                  state.selection_targets.length > 0
                  && state.selection_targets.every(target => selectedSystemIds.has(target.id))
                }
                disabled={!canGenerate || working}
                onChange={event => {
                  setSelectedSystemIds(new Set(
                    event.target.checked
                      ? state.selection_targets.map(target => target.id)
                      : [],
                  ))
                  setSelectionDirty(true)
                }}
                className="h-4 w-4 rounded border-slate-300 text-blue-600"
              />
              Include all
            </label>
          </div>
          <div className="mt-3 space-y-4">
            {([
              ['Navigation Systems', navigationTargets],
              ['Other Target Systems', otherTargets],
            ] as const).map(([label, targets]) => targets.length > 0 && (
              <div key={label}>
                <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</h4>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {targets.map(target => (
                    <TargetSelectionCard
                      key={target.id}
                      target={target}
                      selected={selectedSystemIds.has(target.id)}
                      disabled={!canGenerate || working}
                      onChange={checked => {
                        setSelectedSystemIds(current => {
                          const next = new Set(current)
                          if (checked) next.add(target.id)
                          else next.delete(target.id)
                          return next
                        })
                        setSelectionDirty(true)
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!state.selection_targets.length && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/20 dark:text-amber-300">
          Finalize at least one target system in the Risk Assessment before generating the Test Procedure.
        </section>
      )}

    </div>
  )
}

function TargetSelectionCard({
  target,
  selected,
  disabled,
  onChange,
}: {
  target: TestProcedureState['selection_targets'][number]
  selected: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label
      className={`flex items-start gap-2 rounded-lg border px-3 py-2 ${
        selected
          ? 'cursor-pointer border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/20'
          : 'cursor-pointer border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/40'
      }`}
    >
      <input
        type="checkbox"
        checked={selected}
        disabled={disabled}
        onChange={event => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-blue-600"
      />
      <span className="min-w-0">
        <span className="block text-xs font-medium text-slate-800 dark:text-slate-100">{target.system_name}</span>
        <span className={`mt-0.5 block text-[10px] ${
          selected ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-400'
        }`}>
          {`${selected ? 'Included' : 'Excluded'} · ${target.reusable ? target.resolution_label || 'Approved precedent available' : 'Custom evidence research required'}${target.supplier ? ` · ${target.supplier}` : ''}`}
        </span>
        {target.resolution_provenance?.length > 0 && (
          <span
            className="mt-1 block truncate text-[10px] text-slate-400"
            title={target.resolution_provenance.map(item => item.label).join('\n')}
          >
            Source: {target.resolution_provenance[0].label}
          </span>
        )}
      </span>
    </label>
  )
}

export function ResearchRunPanel({
  run,
  review,
  evidence,
  canEdit,
  working,
  onCancel,
  onAcceptSupported,
  onReviewClaim,
  outputs,
  onGenerateOutput,
  onDownloadOutput,
}: {
  run: TestProcedureResearchRun
  review: TestProcedureResearchReview | null
  evidence: TestProcedureEvidence[]
  canEdit: boolean
  working: boolean
  onCancel: () => Promise<void>
  onAcceptSupported: () => Promise<void>
  onReviewClaim: (claimId: string, status: 'accepted' | 'rejected') => Promise<void>
  outputs: TestProcedureOutput[]
  onGenerateOutput: (mode: 'draft' | 'class-ready') => Promise<void>
  onDownloadOutput: (output: TestProcedureOutput) => Promise<void>
}) {
  const [selectedEvidenceId, setSelectedEvidenceId] = useState<string | null>(null)
  const selectedEvidence = evidence.find(item => item.id === selectedEvidenceId) ?? null
  const progress = run.total_systems
    ? Math.round((run.completed_systems / run.total_systems) * 100)
    : 0
  const readiness = run.readiness
  return (
    <section className="rounded-xl border border-blue-200 bg-blue-50/40 p-4 dark:border-blue-900 dark:bg-blue-950/10">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Evidence research</h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {run.current_system ? `Researching ${run.current_system}` : run.status}
            {` · ${run.completed_systems}/${run.total_systems} systems`}
            {` · $${Number(run.cost_usd).toFixed(2)} USD spent`}
          </p>
        </div>
        <div className="flex gap-2">
          {['queued', 'running'].includes(run.status) && (
            <Button variant="outline" size="sm" disabled={working} onClick={() => void onCancel()}>
              Cancel
            </Button>
          )}
          {run.status === 'completed' && canEdit && (
            <Button size="sm" disabled={working} onClick={() => void onAcceptSupported()}>
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Accept & download TP
            </Button>
          )}
          {run.status === 'completed' && (
            <Button variant="outline" size="sm" disabled={working} onClick={() => void onGenerateOutput('draft')}>
              Generate cited draft
            </Button>
          )}
          {run.status === 'completed' && (
            <Button
              size="sm"
              disabled={working || !run.readiness?.ready}
              onClick={() => void onGenerateOutput('class-ready')}
              title={run.readiness?.ready ? '' : run.readiness?.reasons?.join('\n')}
            >
              Class-ready output
            </Button>
          )}
        </div>
      </div>
      <div className="mt-3 h-2 overflow-hidden rounded-full bg-blue-100 dark:bg-blue-950">
        <div className="h-full bg-blue-500 transition-all" style={{ width: `${progress}%` }} />
      </div>
      {run.error && <p className="mt-3 text-xs text-red-600 dark:text-red-400">{run.error}</p>}

      {review && (
        <>
          <div className="mt-4 grid gap-2 sm:grid-cols-4">
            <StatusCard label="Supported" value={String(review.claims.filter(item => item.support_status === 'supported').length)} ready />
            <StatusCard label="TBU" value={String(review.claims.filter(item => item.support_status === 'tbu').length)} ready={false} />
            <StatusCard label="Conflicts" value={String(review.claims.filter(item => item.support_status === 'conflicting').length)} ready={false} />
            <StatusCard label="Pending review" value={String(review.claims.filter(item => item.review_status === 'pending').length)} ready={false} />
          </div>
          {readiness?.reasons?.length ? (
            <ul className="mt-3 space-y-1 text-xs text-amber-700 dark:text-amber-300">
              {readiness.reasons.map(reason => <li key={reason}>• {reason}</li>)}
            </ul>
          ) : null}
          <div className="mt-4 max-h-[34rem] space-y-2 overflow-y-auto pr-1">
            {review.claims.map(claim => (
              <div key={claim.id} className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900/50">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                    {claim.target_key} · {claim.criterion_code} · {claim.claim_type}
                  </p>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    claim.support_status === 'supported'
                      ? 'bg-emerald-100 text-emerald-700'
                      : claim.support_status === 'conflicting'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                  }`}>{claim.support_status} · {claim.review_status}</span>
                </div>
                <p className="mt-2 text-xs leading-relaxed text-slate-700 dark:text-slate-200">{claim.text}</p>
                {claim.citations.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {claim.citations.map(citation => (
                      <button
                        key={citation.id}
                        type="button"
                        onClick={() => setSelectedEvidenceId(citation.evidence_id)}
                        className={`rounded border px-2 py-1 text-[10px] ${
                          citation.validated
                            ? 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300'
                            : 'border-red-200 bg-red-50 text-red-700'
                        }`}
                        title={citation.quote}
                      >
                        {citation.evidence_label}{citation.page_number ? ` p.${citation.page_number}` : ''}
                      </button>
                    ))}
                  </div>
                )}
                {canEdit && claim.review_status === 'pending' && (
                  <div className="mt-2 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => void onReviewClaim(claim.id, 'accepted')}>Accept</Button>
                    <Button size="sm" variant="outline" onClick={() => void onReviewClaim(claim.id, 'rejected')}>Reject</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {selectedEvidence && (
        <div className="mt-4 rounded-lg border border-slate-300 bg-white p-3 dark:border-slate-700 dark:bg-slate-950">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">{selectedEvidence.label}</p>
              <p className="mt-0.5 text-[10px] uppercase text-slate-400">
                {selectedEvidence.evidence_scope} · {selectedEvidence.evidence_kind}
                {selectedEvidence.page_number ? ` · page ${selectedEvidence.page_number}` : ''}
              </p>
            </div>
            <button type="button" onClick={() => setSelectedEvidenceId(null)} className="text-xs text-slate-400">Close</button>
          </div>
          <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap text-[11px] leading-relaxed text-slate-600 dark:text-slate-300">
            {selectedEvidence.content}
          </pre>
        </div>
      )}

      {outputs.length > 0 && (
        <div className="mt-4">
          <h4 className="text-xs font-semibold text-slate-800 dark:text-slate-100">Immutable outputs</h4>
          <div className="mt-2 space-y-2">
            {outputs.map(output => (
              <div key={output.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 dark:border-slate-700 dark:bg-slate-900/50">
                <div>
                  <p className="text-xs font-medium text-slate-700 dark:text-slate-200">{output.output_filename}</p>
                  <p className="mt-0.5 text-[10px] text-slate-400">
                    {output.verification_status.replace(/_/g, ' ')} · SHA-256 {output.output_sha256.slice(0, 12)}…
                    {' · '}{output.unsupported_count} TBU · {output.conflict_count} conflicts
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => void onDownloadOutput(output)}>
                  <Download className="mr-1.5 h-3.5 w-3.5" /> Download exact file
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  )
}

function SourceNotice({ state }: { state: TestProcedureState }) {
  if (!state.source) return null
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 text-xs dark:border-slate-700 dark:bg-slate-800">
      <div className="flex items-start gap-2">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
        <div>
          <p className="font-semibold text-slate-800 dark:text-slate-100">{state.source.title}</p>
          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Controlled document {state.source.source_document_number}, Rev.{state.source.source_revision}, approved {state.source.source_approved_at}.
            Generated files remain drafts until project-specific review and class acceptance.
          </p>
        </div>
      </div>
    </section>
  )
}

function SourceReviewEditor({
  state,
  canEdit,
  disabled,
  onSave,
}: {
  state: TestProcedureState
  canEdit: boolean
  disabled: boolean
  onSave: (review: TestProcedureSourceReview) => void
}) {
  const draft = state.draft!
  const [review, setReview] = useState<TestProcedureSourceReview>(draft.source_review ?? {})

  useEffect(() => {
    setReview(draft.source_review ?? {})
  }, [draft.id, draft.source_review])

  const setValue = <K extends keyof TestProcedureSourceReview>(
    key: K,
    value: TestProcedureSourceReview[K],
  ) => setReview(current => ({ ...current, [key]: value }))

  return (
    <section className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 dark:border-amber-900 dark:bg-amber-950/20">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-100">
            <AlertTriangle className="h-4 w-4" /> Controlled-source conflict review
          </h3>
          <p className="mt-1 max-w-3xl text-xs text-amber-800 dark:text-amber-200">
            H4299 contains yard-name, ABS rule-edition, document-revision, firewall, cable, and interface inconsistencies.
            Generation stays blocked until they are reconciled against approved project documents.
          </p>
        </div>
        {canEdit && (
          <Button size="sm" disabled={disabled} onClick={() => onSave(review)}>
            <Save className="mr-1.5 h-3.5 w-3.5" /> Save source review
          </Button>
        )}
      </div>

      <div className="mt-3 grid gap-2 lg:grid-cols-2">
        <ReviewCheck
          label="Project, yard, hull, and vessel identity reconciled"
          checked={review.project_identity_confirmed === true}
          disabled={!canEdit}
          onChange={checked => setValue('project_identity_confirmed', checked)}
        />
        <ReviewCheck
          label="Governing ABS edition confirmed despite the source 2024/2025 conflict"
          checked={review.rule_edition_confirmed === true}
          disabled={!canEdit}
          onChange={checked => setValue('rule_edition_confirmed', checked)}
        />
        <ReviewCheck
          label="Approved CSDD, RA, asset inventory, and topology revisions confirmed"
          checked={review.approved_documents_confirmed === true}
          disabled={!canEdit}
          onChange={checked => setValue('approved_documents_confirmed', checked)}
        />
        <ReviewCheck
          label="Source IPs, firewalls, ports, cables, zones, and NMS paths reconciled"
          checked={review.topology_reconciled === true}
          disabled={!canEdit}
          onChange={checked => setValue('topology_reconciled', checked)}
        />
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <TextField
          label="Governing rule-edition basis / approval"
          value={review.rule_edition_basis}
          onChange={value => setValue('rule_edition_basis', value)}
          disabled={!canEdit}
        />
        <TextField
          label="Approved CSDD revision"
          value={review.csdd_revision}
          onChange={value => setValue('csdd_revision', value)}
          disabled={!canEdit}
        />
        <TextField
          label="Approved topology/drawing revision"
          value={review.drawing_revision}
          onChange={value => setValue('drawing_revision', value)}
          disabled={!canEdit}
        />
      </div>
      <label className="mt-3 block text-xs font-medium text-slate-600 dark:text-slate-300">
        Conflict resolution notes / class approval reference
        <textarea
          className={`${fieldClass} mt-1 min-h-20`}
          value={review.review_notes ?? ''}
          disabled={!canEdit}
          onChange={event => setValue('review_notes', event.target.value)}
        />
      </label>
    </section>
  )
}

function StatusCard({ label, value, ready = false }: { label: string; value: string; ready?: boolean }) {
  return (
    <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-900/40">
      <p className="text-[10px] uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`mt-1 font-medium ${ready ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-200'}`}>{value}</p>
    </div>
  )
}

function IntegrationEditor({
  state,
  canEdit,
  disabled,
  onSave,
}: {
  state: TestProcedureState
  canEdit: boolean
  disabled: boolean
  onSave: (
    status: 'pending' | 'applicable' | 'not_applicable',
    reason: string | null,
    config: TestProcedureIntegrationConfig,
  ) => void
}) {
  const draft = state.draft!
  const [status, setStatus] = useState(draft.integration_status)
  const [reason, setReason] = useState(draft.integration_reason ?? '')
  const [config, setConfig] = useState<TestProcedureIntegrationConfig>(draft.integration_config ?? {})

  useEffect(() => {
    setStatus(draft.integration_status)
    setReason(draft.integration_reason ?? '')
    setConfig(draft.integration_config ?? {})
  }, [draft.id, draft.integration_status, draft.integration_reason, draft.integration_config])

  const setField = (key: keyof TestProcedureIntegrationConfig, value: string | number) =>
    setConfig(current => ({ ...current, [key]: value }))
  const boundaries = config.boundaries ?? []
  const updateBoundary = (index: number, patch: Partial<TestProcedureIntegrationBoundary>) =>
    setConfig(current => ({
      ...current,
      boundaries: (current.boundaries ?? []).map((boundary, currentIndex) =>
        currentIndex === index ? { ...boundary, ...patch } : boundary),
    }))

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
            <Network className="h-4 w-4 text-blue-500" /> Vessel integration test
          </h3>
          <p className="mt-1 max-w-3xl text-xs text-slate-500 dark:text-slate-400">
            Do not copy H4299 topology values. Confirm whether integration testing applies, then record this vessel&apos;s reviewed arrangement.
          </p>
        </div>
        {canEdit && (
          <Button size="sm" disabled={disabled} onClick={() => onSave(status, reason.trim() || null, config)}>
            <Save className="mr-1.5 h-3.5 w-3.5" /> Save integration review
          </Button>
        )}
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {([
          ['pending', 'Pending review'],
          ['applicable', 'Applicable'],
          ['not_applicable', 'Not applicable'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            disabled={!canEdit}
            onClick={() => setStatus(value)}
            className={`rounded-lg border px-3 py-2 text-left text-xs font-medium ${
              status === value
                ? 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                : 'border-slate-200 text-slate-600 dark:border-slate-700 dark:text-slate-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {status === 'not_applicable' ? (
        <label className="mt-3 block text-xs font-medium text-slate-600 dark:text-slate-300">
          Basis for non-applicability and approval reference
          <textarea className={`${fieldClass} mt-1 min-h-20`} value={reason} onChange={event => setReason(event.target.value)} disabled={!canEdit} />
        </label>
      ) : status === 'applicable' ? (
        <div className="mt-4 space-y-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <TextField label="Test location" value={config.location} onChange={value => setField('location', value)} disabled={!canEdit} />
            <TextField label="Network monitoring system" value={config.monitoringSystem} onChange={value => setField('monitoringSystem', value)} disabled={!canEdit} />
            <TextField label="Monitoring model" value={config.monitoringModel} onChange={value => setField('monitoringModel', value)} disabled={!canEdit} />
            <TextField label="Monitoring address" value={config.monitoringAddress} onChange={value => setField('monitoringAddress', value)} disabled={!canEdit} />
            <TextField label="Management network" value={config.managementNetwork} onChange={value => setField('managementNetwork', value)} disabled={!canEdit} />
            <TextField label="Gateway" value={config.gateway} onChange={value => setField('gateway', value)} disabled={!canEdit} />
            <TextField label="Main boundary device" value={config.mainBoundaryDevice} onChange={value => setField('mainBoundaryDevice', value)} disabled={!canEdit} />
            <TextField label="Main boundary model" value={config.mainBoundaryModel} onChange={value => setField('mainBoundaryModel', value)} disabled={!canEdit} />
            <TextField label="Main boundary address" value={config.mainBoundaryAddress} onChange={value => setField('mainBoundaryAddress', value)} disabled={!canEdit} />
            <TextField label="Test source / laptop" value={config.testSource} onChange={value => setField('testSource', value)} disabled={!canEdit} />
            <TextField
              label="Controlled flood rate (Mbps)"
              value={String(config.floodRateMbps ?? 100)}
              onChange={value => setField('floodRateMbps', Number(value) || 0)}
              disabled={!canEdit}
              type="number"
            />
          </div>

          <div>
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-200">Reviewed zone-boundary paths</h4>
              {canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfig(current => ({
                    ...current,
                    boundaries: [...(current.boundaries ?? []), { zone: '', device: '', connectedSystems: [] }],
                  }))}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" /> Add path
                </Button>
              )}
            </div>
            <div className="mt-2 space-y-2">
              {boundaries.map((boundary, index) => (
                <div key={index} className="grid gap-2 rounded-lg border border-slate-200 p-3 md:grid-cols-2 xl:grid-cols-6 dark:border-slate-700">
                  <TextField label="Zone" value={boundary.zone} onChange={value => updateBoundary(index, { zone: value })} disabled={!canEdit} />
                  <TextField label="Boundary device" value={boundary.device} onChange={value => updateBoundary(index, { device: value })} disabled={!canEdit} />
                  <TextField label="Model" value={boundary.model} onChange={value => updateBoundary(index, { model: value })} disabled={!canEdit} />
                  <TextField label="Address" value={boundary.address} onChange={value => updateBoundary(index, { address: value })} disabled={!canEdit} />
                  <TextField label="Protected subnet" value={boundary.subnet} onChange={value => updateBoundary(index, { subnet: value })} disabled={!canEdit} />
                  <div className="flex items-end gap-1">
                    <TextField
                      label="Connected systems"
                      value={boundary.connectedSystems?.join(', ') ?? ''}
                      onChange={value => updateBoundary(index, { connectedSystems: value.split(',').map(item => item.trim()).filter(Boolean) })}
                      disabled={!canEdit}
                    />
                    {canEdit && (
                      <button
                        type="button"
                        title="Remove path"
                        onClick={() => setConfig(current => ({
                          ...current,
                          boundaries: (current.boundaries ?? []).filter((_, currentIndex) => currentIndex !== index),
                        }))}
                        className="mb-0.5 rounded p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {!boundaries.length && <p className="text-xs text-amber-600">Add each firewall or security-zone boundary path selected for integration testing.</p>}
            </div>
          </div>
        </div>
      ) : null}
    </section>
  )
}

function SystemMappingEditor({
  target,
  sourceSystems,
  canEdit,
  disabled,
  onSave,
}: {
  target: TestProcedureTarget
  sourceSystems: TestProcedureSourceSystem[]
  canEdit: boolean
  disabled: boolean
  onSave: (
    sourceSystemKey: string,
    overrides: { model?: string; pda?: string; location?: string },
    applicabilityReview: TestProcedureSystemApplicabilityReview,
    notes: string | null,
  ) => void
}) {
  const suggestedKey = target.mapping?.source_system_key ?? target.candidates[0]?.source_system_key ?? ''
  const [sourceKey, setSourceKey] = useState(suggestedKey)
  const [model, setModel] = useState(target.mapping?.overrides?.model ?? '')
  const [pda, setPda] = useState(target.mapping?.overrides?.pda ?? '')
  const [location, setLocation] = useState(target.mapping?.overrides?.location ?? '')
  const [notes, setNotes] = useState(target.mapping?.review_notes ?? '')
  const [applicabilityReview, setApplicabilityReview] = useState<TestProcedureSystemApplicabilityReview>(
    target.mapping?.applicability_review ?? {},
  )
  const approved = target.mapping?.review_status === 'approved'
  const selected = sourceSystems.find(system => system.key === sourceKey)

  useEffect(() => {
    setSourceKey(target.mapping?.source_system_key ?? target.candidates[0]?.source_system_key ?? '')
    setModel(target.mapping?.overrides?.model ?? '')
    setPda(target.mapping?.overrides?.pda ?? '')
    setLocation(target.mapping?.overrides?.location ?? '')
    setNotes(target.mapping?.review_notes ?? '')
    setApplicabilityReview(target.mapping?.applicability_review ?? {})
  }, [target])

  const setReview = <K extends keyof TestProcedureSystemApplicabilityReview>(
    key: K,
    value: TestProcedureSystemApplicabilityReview[K],
  ) => setApplicabilityReview(current => ({ ...current, [key]: value }))
  const reviewComplete = Boolean(
    applicabilityReview.installed_configuration_confirmed &&
    applicabilityReview.capability_applicability_confirmed &&
    applicabilityReview.network_path_confirmed &&
    (!selected?.pda || applicabilityReview.pda_scope_confirmed) &&
    applicabilityReview.integration_dependencies_resolved &&
    applicabilityReview.test_safety_confirmed &&
    (!selected?.review_flags.length || (
      applicabilityReview.known_conflicts_resolved &&
      notes.trim()
    ))
  )

  return (
    <article className={`rounded-xl border bg-white p-4 dark:bg-slate-800 ${approved ? 'border-emerald-200 dark:border-emerald-900' : 'border-slate-200 dark:border-slate-700'}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold text-slate-900 dark:text-white">{target.system_name}</h4>
            {approved && <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">Reviewed</span>}
          </div>
          <p className="mt-0.5 text-xs text-slate-500">{target.supplier || 'Supplier TBU'} · {target.ship_function || 'Function TBU'}</p>
        </div>
        {target.mapping?.similarity_score != null && (
          <span className="text-[10px] text-slate-400">Suggested match {Math.round(target.mapping.similarity_score * 100)}%</span>
        )}
      </div>

      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        <label className="text-xs font-medium text-slate-600 dark:text-slate-300">
          Approved source section
          <select
            className={`${fieldClass} mt-1`}
            value={sourceKey}
            onChange={event => {
              setSourceKey(event.target.value)
              setApplicabilityReview({})
            }}
            disabled={!canEdit}
          >
            <option value="">Select a controlled section</option>
            {sourceSystems.map(system => (
              <option key={system.key} value={system.key}>
                {system.system} · {system.supplier} · {system.model}
              </option>
            ))}
          </select>
        </label>
        <div className="rounded-lg bg-slate-50 p-3 text-xs text-slate-500 dark:bg-slate-900/40 dark:text-slate-400">
          {selected ? (
            <>
              <p><span className="font-medium text-slate-700 dark:text-slate-200">Source:</span> {selected.system} / {selected.supplier}</p>
              <p className="mt-1">Model {selected.model || 'TBU'} · PDA {selected.pda || 'N/A'} · pages {selected.source_pages.start}–{selected.source_pages.end}</p>
              {selected.topology_anchors.length > 0 && (
                <p className="mt-1 text-amber-700 dark:text-amber-300">
                  Source-specific anchors: {selected.topology_anchors.join(', ')}
                </p>
              )}
            </>
          ) : 'Select the approved section that matches the installed system.'}
        </div>
      </div>
      <div className="mt-3 grid gap-3 md:grid-cols-3">
        <TextField label="Installed model override" value={model} onChange={setModel} disabled={!canEdit} placeholder={selected?.model || 'TBU'} />
        <TextField label="Project PDA override" value={pda} onChange={setPda} disabled={!canEdit} placeholder={selected?.pda || 'N/A'} />
        <TextField label="Installation location" value={location} onChange={setLocation} disabled={!canEdit} />
      </div>
      {selected && (
        <div className="mt-3 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
          {selected.review_flags.length > 0 && (
            <div className="mb-3 rounded-md bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
              <p className="font-semibold">Controlled-source exceptions</p>
              <ul className="mt-1 space-y-1">
                {selected.review_flags.map(flag => <li key={flag}>• {flag}</li>)}
              </ul>
            </div>
          )}
          <div className="grid gap-2 lg:grid-cols-2">
            <ReviewCheck
              label="Installed model, software, interfaces, and location confirmed"
              checked={applicabilityReview.installed_configuration_confirmed === true}
              disabled={!canEdit}
              onChange={checked => setReview('installed_configuration_confirmed', checked)}
            />
            <ReviewCheck
              label="System capabilities and each test criterion applicability confirmed"
              checked={applicabilityReview.capability_applicability_confirmed === true}
              disabled={!canEdit}
              onChange={checked => setReview('capability_applicability_confirmed', checked)}
            />
            <ReviewCheck
              label="IP boundary, internal Ethernet, serial/CAN, hardwired, or isolated path confirmed"
              checked={applicabilityReview.network_path_confirmed === true}
              disabled={!canEdit}
              onChange={checked => setReview('network_path_confirmed', checked)}
            />
            {selected.pda && (
              <ReviewCheck
                label="PDA scope matches installed model, version, and topology"
                checked={applicabilityReview.pda_scope_confirmed === true}
                disabled={!canEdit}
                onChange={checked => setReview('pda_scope_confirmed', checked)}
              />
            )}
            <ReviewCheck
              label="Integration dependencies resolve to reviewed project paths and evidence"
              checked={applicabilityReview.integration_dependencies_resolved === true}
              disabled={!canEdit}
              onChange={checked => setReview('integration_dependencies_resolved', checked)}
            />
            <ReviewCheck
              label="Intrusive rates, disconnections, rollback, and witness conditions reviewed"
              checked={applicabilityReview.test_safety_confirmed === true}
              disabled={!canEdit}
              onChange={checked => setReview('test_safety_confirmed', checked)}
            />
            {selected.review_flags.length > 0 && (
              <ReviewCheck
                label="Listed controlled-source exceptions resolved and documented below"
                checked={applicabilityReview.known_conflicts_resolved === true}
                disabled={!canEdit}
                onChange={checked => setReview('known_conflicts_resolved', checked)}
              />
            )}
          </div>
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="min-w-[260px] flex-1 text-xs font-medium text-slate-600 dark:text-slate-300">
          Review notes
          <input className={`${fieldClass} mt-1`} value={notes} onChange={event => setNotes(event.target.value)} disabled={!canEdit} />
        </label>
        {canEdit && (
          <Button
            size="sm"
            disabled={disabled || !sourceKey || !reviewComplete}
            onClick={() => onSave(
              sourceKey,
              {
                model: model.trim() || undefined,
                pda: pda.trim() || undefined,
                location: location.trim() || undefined,
              },
              applicabilityReview,
              notes.trim() || null,
            )}
          >
            <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Approve section
          </Button>
        )}
      </div>
    </article>
  )
}

function ReviewCheck({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex items-start gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-200">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={event => onChange(event.target.checked)}
        className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-blue-600"
      />
      <span>{label}</span>
    </label>
  )
}

function TextField({
  label,
  value,
  onChange,
  disabled,
  placeholder,
  type = 'text',
}: {
  label: string
  value: string | null | undefined
  onChange: (value: string) => void
  disabled: boolean
  placeholder?: string
  type?: string
}) {
  return (
    <label className="min-w-0 flex-1 text-xs font-medium text-slate-600 dark:text-slate-300">
      {label}
      <input
        type={type}
        className={`${fieldClass} mt-1`}
        value={value ?? ''}
        placeholder={placeholder}
        disabled={disabled}
        onChange={event => onChange(event.target.value)}
      />
    </label>
  )
}
